import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AppShell } from '../components/AppShell'
import { MarkPicker } from '../components/attendance/MarkPicker'
import {
  AttendanceStats,
  AttendanceLegend,
  computeAttendanceCounts,
} from '../components/attendance/AttendanceStats'
import { useEntityList, useEntitySave, makeEntity, now } from '../api/hooks/useEntity'
import {
  ENTITY,
  type Attendance,
  type AttendanceMark,
  type SchoolClass,
  type Student,
} from '../types/entities'
import { localIsoDate } from '../utils/dates'

/**
 * Журнал посещаемости: на одну дату — отметка каждого ученика.
 * Page-компонент: state классов и даты, индексация marks, save через
 * useEntitySave. Markpicker и stats живут в `components/attendance/`.
 */
export function AttendancePage() {
  const { t } = useTranslation()
  const classes = useEntityList<SchoolClass>(ENTITY.schoolClass)
  const students = useEntityList<Student>(ENTITY.student)
  const attendance = useEntityList<Attendance>(ENTITY.attendance)
  const save = useEntitySave<Attendance>(ENTITY.attendance)

  const activeClasses = useMemo(
    () =>
      (classes.data ?? [])
        .filter((c) => c.archivedAt == null)
        .sort((a, b) => a.gradeLevel - b.gradeLevel || a.name.localeCompare(b.name)),
    [classes.data],
  )

  const [classId, setClassId] = useState<string>('')
  const [date, setDate] = useState<string>(() => localIsoDate())

  useEffect(() => {
    if (!classId && activeClasses[0]) setClassId(activeClasses[0].id)
  }, [classId, activeClasses])

  const classStudents = useMemo(
    () =>
      (students.data ?? [])
        .filter((s) => s.classId === classId)
        .sort((a, b) =>
          a.lastName.localeCompare(b.lastName) || a.firstName.localeCompare(b.firstName),
        ),
    [students.data, classId],
  )

  const marksByStudent = useMemo(() => {
    const m = new Map<string, Attendance>()
    for (const a of attendance.data ?? []) {
      if (a.classId === classId && a.date === date && !a.subjectId) {
        m.set(a.studentId, a)
      }
    }
    return m
  }, [attendance.data, classId, date])

  const counts = useMemo(() => computeAttendanceCounts(classStudents, marksByStudent), [classStudents, marksByStudent])

  const setMark = (studentId: string, mark: AttendanceMark) => {
    const existing = marksByStudent.get(studentId)
    save.mutate(
      makeEntity<Attendance>({
        ...(existing ?? {}),
        classId,
        studentId,
        date,
        mark,
        updatedAt: now(),
      } as Attendance),
    )
  }

  if (activeClasses.length === 0) {
    return (
      <AppShell title={t('attendancePage.title')}>
        <NoClasses />
      </AppShell>
    )
  }

  return (
    <AppShell title={t('attendancePage.title')} subtitle={t('attendancePage.subtitle')}>
      <FilterBar
        activeClasses={activeClasses}
        classId={classId}
        onClassChange={setClassId}
        date={date}
        onDateChange={setDate}
      />

      {classStudents.length > 0 && <AttendanceStats counts={counts} />}

      {classStudents.length === 0 ? (
        <NoStudents />
      ) : (
        <>
          <div className="bg-white border border-gray-200 rounded-xl divide-y divide-gray-100">
            {classStudents.map((s) => (
              <StudentRow
                key={s.id}
                student={s}
                mark={marksByStudent.get(s.id)?.mark}
                onMarkChange={(m) => setMark(s.id, m)}
              />
            ))}
          </div>
          <AttendanceLegend />
        </>
      )}
    </AppShell>
  )
}

function FilterBar({
  activeClasses,
  classId,
  onClassChange,
  date,
  onDateChange,
}: {
  activeClasses: SchoolClass[]
  classId: string
  onClassChange: (id: string) => void
  date: string
  onDateChange: (d: string) => void
}) {
  const { t } = useTranslation()
  return (
    <div className="flex gap-2 mb-5 flex-wrap items-center">
      <select className="input-field max-w-[200px]" value={classId} onChange={(e) => onClassChange(e.target.value)}>
        {activeClasses.map((c) => (
          <option key={c.id} value={c.id}>{c.name} ({t('attendancePage.classOption', { grade: c.gradeLevel })})</option>
        ))}
      </select>
      <input
        type="date"
        className="input-field max-w-[180px]"
        value={date}
        onChange={(e) => onDateChange(e.target.value)}
      />
    </div>
  )
}

function StudentRow({
  student,
  mark,
  onMarkChange,
}: {
  student: Student
  mark: AttendanceMark | undefined
  onMarkChange: (m: AttendanceMark) => void
}) {
  return (
    <div className="flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50">
      <div className="flex-1 min-w-0">
        <div className="font-medium text-gray-900">
          {student.lastName} {student.firstName}
          {student.middleName && <span className="text-gray-500 ml-1">{student.middleName}</span>}
        </div>
      </div>
      <MarkPicker selected={mark} onChange={onMarkChange} />
    </div>
  )
}

function NoClasses() {
  const { t } = useTranslation()
  return (
    <div className="text-center py-12 bg-gray-50 border border-gray-200 rounded-2xl">
      <div className="text-5xl mb-3">📘</div>
      <h3 className="font-semibold text-gray-900 mb-2">{t('attendancePage.noClassesTitle')}</h3>
      <a href="/app/classes" className="btn-primary text-sm inline-block">{t('attendancePage.toClasses')}</a>
    </div>
  )
}

function NoStudents() {
  const { t } = useTranslation()
  return (
    <div className="text-center py-12 text-gray-500">
      {t('attendancePage.noStudents')}{' '}
      <a href="/app/students" className="text-primary hover:underline">{t('attendancePage.addStudent')}</a>
    </div>
  )
}
